// FLAC (Free Lossless Audio Codec) parser.
//
// Layout: [ID3v2 tag(s), non-standard] "fLaC" · metadata blocks · audio frames ·
// [APE tag] [ID3v1 tag]. Each frame holds one block of samples for all channels,
// coded as one subframe per channel. Subframes are bit-packed, so we decode their
// structure (but not the samples) to find where each one ends.
import {
  BitFields, ascii, channelName, field, fmtBytes, fmtHz, fmtKbps, fmtSec, hex, matches, plural,
  synchsafe32, u16be, u24be, u32be, u32le, utf8Str,
} from './bits';
import { id3Explanations, parseId3v1, parseId3v2 } from './id3';
import type { AudioInfo, Explanation, Field, FormatParser, Note, ParsedFile, Picture, Segment, SegmentKind, Tag } from './types';
import { ParseError } from './types';
import { parseFlacPicture, parseVorbisComment, vorbisCommentExplanations } from './vorbiscomment';

// ---------------------------------------------------------------------------
// CRCs. CRC-8 (poly 0x07) protects each frame header; CRC-16 (poly 0x8005)
// protects the whole frame. CRC-16 uses "slice-by-4" tables because we check
// every frame during the main parse.

const CRC8 = new Uint8Array(256);
const C16 = [0, 1, 2, 3].map(() => new Uint16Array(256));
for (let i = 0; i < 256; i++) {
  let c = i;
  for (let k = 0; k < 8; k++) c = c & 0x80 ? ((c << 1) ^ 0x07) & 0xff : (c << 1) & 0xff;
  CRC8[i] = c;
  let d = i << 8;
  for (let k = 0; k < 8; k++) d = d & 0x8000 ? ((d << 1) ^ 0x8005) & 0xffff : (d << 1) & 0xffff;
  C16[0][i] = d;
}
// C16[k][i] = CRC of byte i followed by k zero bytes.
for (let k = 1; k < 4; k++) for (let i = 0; i < 256; i++) { const p = C16[k - 1][i]; C16[k][i] = ((p << 8) & 0xffff) ^ C16[0][p >> 8]; }
const [T0, T1, T2, T3] = C16;

export function crc8(b: Uint8Array, start: number, end: number): number {
  let c = 0;
  for (let i = start; i < end; i++) c = CRC8[c ^ b[i]];
  return c;
}
export function crc16(b: Uint8Array, start: number, end: number): number {
  let c = 0, i = start;
  for (; i + 3 < end; i += 4) c = T3[(c >> 8) ^ b[i]] ^ T2[(c & 0xff) ^ b[i + 1]] ^ T1[b[i + 2]] ^ T0[b[i + 3]];
  for (; i < end; i++) c = ((c << 8) & 0xffff) ^ T0[(c >> 8) ^ b[i]];
  return c;
}

// ---------------------------------------------------------------------------
// Lookup tables.

const BLOCK_TYPES: Record<number, { name: string; label: string; kind: SegmentKind; type: string }> = {
  0: { name: 'STREAMINFO', label: 'Stream info', kind: 'header', type: 'flac.streaminfo' },
  1: { name: 'PADDING', label: 'Padding', kind: 'padding', type: 'flac.padding' },
  2: { name: 'APPLICATION', label: 'Application data', kind: 'metadata', type: 'flac.application' },
  3: { name: 'SEEKTABLE', label: 'Seek table', kind: 'index', type: 'flac.seektable' },
  4: { name: 'VORBIS_COMMENT', label: 'Vorbis comments', kind: 'metadata', type: 'flac.vorbis_comment' },
  5: { name: 'CUESHEET', label: 'Cue sheet', kind: 'index', type: 'flac.cuesheet' },
  6: { name: 'PICTURE', label: 'Picture', kind: 'image', type: 'flac.picture' },
};

// Registered APPLICATION block IDs (from the FLAC format registry).
const APP_IDS: Record<string, string> = {
  ATCH: 'FlacFile', BSOL: 'beSolo', BUGS: 'Bugs Player', Cues: 'GoldWave cue points', Fica: 'CUE Splitter',
  Ftol: 'flac-tools', MOTB: 'MOTB MetaCzar', MPSE: 'MP3 Stream Editor', MuML: 'MusicML', RIFF: 'Sound Devil RIFF storage',
  SFFL: 'Sound Font FLAC', SONY: 'Sony Creative Software', SQEZ: 'flacsqueeze', TtWv: 'TwistedWave',
  UITS: 'UITS embedding tools', aiff: 'AIFF chunks (flac --keep-foreign-metadata)', riff: 'RIFF/WAV chunks (flac --keep-foreign-metadata)',
  'w64 ': 'Wave64 chunks (flac --keep-foreign-metadata)', imag: 'flac-image', peem: 'Parseable Embedded Extensible Metadata',
  qfst: 'QFLAC Studio', tune: 'TagTuner', xbat: 'XBAT', xmcd: 'xmcd',
};

const SR_TABLE = [0, 88200, 176400, 192000, 8000, 16000, 22050, 24000, 32000, 44100, 48000, 96000];
const BPS_TABLE = [0, 8, 12, -1, 16, 20, 24, 32];

const ASSIGN_NAMES = ['Mono', 'Left + right, coded separately', '3 channels, coded separately', '4 channels, coded separately',
  '5 channels, coded separately', '6 channels, coded separately', '7 channels, coded separately', '8 channels, coded separately',
  'Left/side stereo', 'Right/side stereo', 'Mid/side stereo'];
const ASSIGN_SHORT = ['Mono', 'L/R', '3 ch', '4 ch', '5 ch', '6 ch', '7 ch', '8 ch', 'Left/side', 'Right/side', 'Mid/side'];

const LAYOUTS = [
  ['Mono'], ['Left', 'Right'], ['Left', 'Right', 'Center'], ['Front left', 'Front right', 'Back left', 'Back right'],
  ['Front left', 'Front right', 'Center', 'Back left', 'Back right'],
  ['Front left', 'Front right', 'Center', 'LFE', 'Back left', 'Back right'],
  ['Front left', 'Front right', 'Center', 'LFE', 'Back center', 'Side left', 'Side right'],
  ['Front left', 'Front right', 'Center', 'LFE', 'Back left', 'Back right', 'Side left', 'Side right'],
];

/** Name of channel `ch` in a frame with channel assignment `a`. */
function channelLabel(a: number, ch: number): string {
  if (a === 8) return ch === 0 ? 'Left' : 'Side (left − right)';
  if (a === 9) return ch === 0 ? 'Side (left − right)' : 'Right';
  if (a === 10) return ch === 0 ? 'Mid (average of left and right)' : 'Side (left − right)';
  return LAYOUTS[a]?.[ch] ?? `Channel ${ch + 1}`;
}
/** The side channel needs one extra bit, because left − right can be twice as large as either. */
function sideIndex(a: number): number {
  return a === 8 || a === 10 ? 1 : a === 9 ? 0 : -1;
}

// ---------------------------------------------------------------------------
// Data shapes.

export interface StreamInfo {
  minBlock: number; maxBlock: number; minFrame: number; maxFrame: number;
  sampleRate: number; channels: number; bps: number; totalSamples: number; md5Set: boolean;
}

/** A frame header as found by the scanner. Kept small: one per frame. */
export interface FrameHeader {
  start: number;
  headerLen: number;
  /** Length of the UTF-8-style coded number, in bytes. */
  numLen: number;
  variable: boolean;
  bsCode: number; srCode: number; assignment: number; bpsCode: number;
  blockSize: number;
  /** 0 when the header says "use STREAMINFO" and there is no STREAMINFO. */
  sampleRate: number;
  bps: number;
  channels: number;
  /** Frame number (fixed block size) or first sample number (variable block size). */
  number: number;
}

interface Ctx {
  b: Uint8Array;
  si: StreamInfo | null;
  cache: WeakMap<Segment, { fields: Field[]; children: Segment[] }>;
}

// ---------------------------------------------------------------------------
// Frame header parsing (hot path during the scan: no allocation until valid).

export function parseFrameHeader(b: Uint8Array, p: number, limit: number, si: StreamInfo | null): FrameHeader | null {
  if (p + 6 > limit) return null;
  const b1 = b[p + 1];
  if (b[p] !== 0xff || (b1 & 0xfe) !== 0xf8) return null;
  const b2 = b[p + 2], b3 = b[p + 3];
  const bsCode = b2 >> 4, srCode = b2 & 15;
  const assignment = b3 >> 4, bpsCode = (b3 >> 1) & 7;
  if (bsCode === 0 || srCode === 15 || assignment > 10 || bpsCode === 3 || b3 & 1) return null;
  const variable = (b1 & 1) === 1;
  // The coded number uses the UTF-8 pattern: the count of leading 1 bits in the
  // first byte gives the total length, and each extra byte carries 6 bits.
  let q = p + 4;
  const x = b[q++];
  let num: number, extra: number;
  if (x < 0x80) { num = x; extra = 0; }
  else if ((x & 0xe0) === 0xc0) { num = x & 0x1f; extra = 1; }
  else if ((x & 0xf0) === 0xe0) { num = x & 0x0f; extra = 2; }
  else if ((x & 0xf8) === 0xf0) { num = x & 0x07; extra = 3; }
  else if ((x & 0xfc) === 0xf8) { num = x & 0x03; extra = 4; }
  else if ((x & 0xfe) === 0xfc) { num = x & 0x01; extra = 5; }
  else if (x === 0xfe && variable) { num = 0; extra = 6; }
  else return null;
  if (q + extra + 3 > limit) return null;
  for (let i = 0; i < extra; i++) {
    const y = b[q++];
    if ((y & 0xc0) !== 0x80) return null;
    num = num * 64 + (y & 0x3f);
  }
  const numLen = extra + 1;
  let blockSize: number;
  if (bsCode === 1) blockSize = 192;
  else if (bsCode <= 5) blockSize = 576 << (bsCode - 2);
  else if (bsCode === 6) blockSize = b[q++] + 1;
  else if (bsCode === 7) { blockSize = u16be(b, q) + 1; q += 2; }
  else blockSize = 256 << (bsCode - 8);
  let sampleRate: number;
  if (srCode === 0) sampleRate = si?.sampleRate ?? 0;
  else if (srCode < 12) sampleRate = SR_TABLE[srCode];
  else if (srCode === 12) sampleRate = b[q++] * 1000;
  else if (srCode === 13) { sampleRate = u16be(b, q); q += 2; }
  else { sampleRate = u16be(b, q) * 10; q += 2; }
  if (q >= limit || crc8(b, p, q) !== b[q]) return null;
  const bps = bpsCode === 0 ? si?.bps ?? 0 : BPS_TABLE[bpsCode];
  const channels = assignment < 8 ? assignment + 1 : 2;
  return { start: p, headerLen: q + 1 - p, numLen, variable, bsCode, srCode, assignment, bpsCode, blockSize, sampleRate, bps, channels, number: num };
}

/** A header can have a valid CRC-8 by chance; it must also agree with STREAMINFO. */
function consistent(h: FrameHeader, si: StreamInfo | null): boolean {
  if (!si) return h.bps > 0;
  if (h.channels !== si.channels) return false;
  if (h.srCode !== 0 && si.sampleRate > 0 && h.sampleRate !== si.sampleRate) return false;
  if (h.bpsCode !== 0 && h.bps !== si.bps) return false;
  if (si.maxBlock >= 16 && h.blockSize > si.maxBlock) return false;
  return h.bps > 0;
}

// ---------------------------------------------------------------------------
// Fast bit reader for subframes. Positions are relative to a frame-sized
// subarray so they always fit in 31 bits.

class FastBits {
  readonly endBit: number;
  constructor(readonly b: Uint8Array, public pos: number) {
    this.endBit = b.length * 8;
  }
  read(n: number): number {
    if (n === 0) return 0;
    let p = this.pos;
    if (p + n > this.endBit) throw new RangeError('The data runs past the end of the frame.');
    let v = 0, need = n;
    while (need > 0) {
      const avail = 8 - (p & 7), take = avail < need ? avail : need;
      v = v * (1 << take) + ((this.b[p >> 3] >> (avail - take)) & ((1 << take) - 1));
      p += take;
      need -= take;
    }
    this.pos = p;
    return v;
  }
  signed(n: number): number {
    if (n === 0) return 0;
    const v = this.read(n);
    return v >= 2 ** (n - 1) ? v - 2 ** n : v;
  }
  /** Counts 0 bits up to (and consumes) the next 1 bit. */
  unary(): number {
    const b = this.b, start = this.pos;
    let i = start >> 3;
    let cur = b[i] & (0xff >> (start & 7));
    while (cur === 0) {
      if (++i >= b.length) throw new RangeError('A unary code runs past the end of the frame.');
      cur = b[i];
    }
    const one = i * 8 + Math.clz32(cur) - 24;
    this.pos = one + 1;
    return one - start;
  }
  skip(n: number): void {
    if (this.pos + n > this.endBit) throw new RangeError('The data runs past the end of the frame.');
    this.pos += n;
  }
}

/** Skips `count` Rice codes with parameter k. Each code is a unary quotient, a 1 bit, then k low bits. */
export function skipRice(fb: Uint8Array, pos: number, count: number, k: number): number {
  const len = fb.length;
  for (let s = 0; s < count; s++) {
    let i = pos >> 3;
    let cur = fb[i] & (0xff >> (pos & 7));
    while (cur === 0) {
      if (++i >= len) throw new RangeError('Rice codes run past the end of the frame.');
      cur = fb[i];
    }
    pos = i * 8 + Math.clz32(cur) - 23 + k;
  }
  if (pos > len * 8) throw new RangeError('Rice codes run past the end of the frame.');
  return pos;
}

// ---------------------------------------------------------------------------
// Subframe decoding (structure only).

interface SubframeResult {
  fields: Field[];
  startBit: number; // frame-relative
  endBit: number; // frame-relative; valid when !error
  type: string;
  summary: string;
  error?: string;
}

/** Only the first partitions get their own fields; the rest are grouped. */
const MAX_PARTITION_FIELDS = 256;

function decodeSubframe(fb: Uint8Array, startBit: number, blockSize: number, bps: number, base: number): SubframeResult {
  const r = new FastBits(fb, startBit);
  const fields: Field[] = [];
  const add = (name: string, value: string, bit: number, bits: number, note?: string, raw?: number) => {
    if (bits > 0) fields.push({ name, value, raw, bit: base + bit, bits, note });
  };
  const res: SubframeResult = { fields, startBit, endBit: startBit, type: 'flac.subframe.error', summary: '' };
  try {
    let p = r.pos;
    const zero = r.read(1);
    add('Zero bit', String(zero), p, 1, zero ? 'Must be 0. A 1 here means the data is damaged.' : 'Always 0. It helps to keep a subframe from looking like a frame sync code.', zero);
    p = r.pos;
    const t = r.read(6);
    const typeName = t === 0 ? 'CONSTANT' : t === 1 ? 'VERBATIM' : t >= 8 && t <= 12 ? `FIXED, order ${t - 8}` : t >= 32 ? `LPC, order ${t - 31}` : 'Reserved';
    add('Subframe type', `${typeName} (${t.toString(2).padStart(6, '0')})`, p, 6, 'Which method codes this channel: one constant value, raw samples, a fixed predictor, or an LPC predictor.', t);
    p = r.pos;
    const wflag = r.read(1);
    add('Wasted bits flag', wflag ? 'Yes' : 'No', p, 1, 'Set when every sample ends in some zero bits, which are then not stored.', wflag);
    let wasted = 0;
    if (wflag) {
      p = r.pos;
      wasted = r.unary() + 1;
      add('Wasted bits', `${wasted} (unary)`, p, r.pos - p, 'How many low zero bits every sample has. The decoder shifts the samples left by this amount.', wasted);
    }
    const sb = bps - wasted;
    if (sb < 1) throw new RangeError(`Wasted bits (${wasted}) leave no bits per sample.`);
    const wastedNote = wasted ? ` · ${plural(wasted, 'wasted bit')}` : '';

    const warmup = (order: number) => {
      for (let i = 0; i < order; i++) {
        p = r.pos;
        const v = r.signed(sb);
        add(`Warm-up sample ${i + 1}`, v.toLocaleString('en-US'), p, sb,
          i === 0 ? `Stored as-is in ${sb} bits. The predictor needs ${plural(order, 'earlier sample')} before it can start.` : undefined, v);
      }
    };

    if (t === 0) {
      res.type = 'flac.subframe.constant';
      p = r.pos;
      const v = r.signed(sb);
      add('Constant value', v.toLocaleString('en-US'), p, sb, `Every one of the ${blockSize.toLocaleString('en-US')} samples has this value.`, v);
      res.summary = `CONSTANT ${v.toLocaleString('en-US')} · ${plural(r.pos - startBit, 'bit')}${wastedNote}`;
    } else if (t === 1) {
      res.type = 'flac.subframe.verbatim';
      p = r.pos;
      r.skip(sb * blockSize);
      add('Samples', `${blockSize.toLocaleString('en-US')} samples × ${sb} bits`, p, sb * blockSize, 'Raw samples with no compression.');
      res.summary = `VERBATIM · ${plural(r.pos - startBit, 'bit')}${wastedNote}`;
    } else if ((t >= 8 && t <= 12) || t >= 32) {
      const lpc = t >= 32;
      const order = lpc ? t - 31 : t - 8;
      if (order > blockSize) throw new RangeError(`Predictor order ${order} is larger than the block size.`);
      res.type = lpc ? 'flac.subframe.lpc' : 'flac.subframe.fixed';
      warmup(order);
      if (lpc) {
        p = r.pos;
        const prec = r.read(4);
        if (prec === 15) throw new RangeError('Coefficient precision 1111 is not allowed.');
        add('Coefficient precision', `${prec + 1} bits`, p, 4, 'Size of each coefficient below, stored as bits − 1.', prec);
        p = r.pos;
        const shift = r.signed(5);
        add('Quantization shift', `${shift} bits`, p, 5,
          shift < 0 ? 'A negative shift is not allowed by the format.' : `The predictor divides its sum by 2^${shift}, so the whole-number coefficients act like fractions.`, shift);
        for (let i = 0; i < order; i++) {
          p = r.pos;
          const c = r.signed(prec + 1);
          add(`Coefficient ${i + 1}`, c.toLocaleString('en-US'), p, prec + 1,
            i === 0 ? 'Weights for the previous samples. The prediction is a weighted sum of recent samples.' : undefined, c);
        }
      }
      const rs = decodeResidual(r, fb, blockSize, order, add);
      const kind = lpc ? `LPC order ${order}` : `FIXED order ${order}`;
      res.summary = `${kind} · ${rs.method} partitions 2^${rs.partitionOrder} · ${plural(r.pos - startBit, 'bit')}${wastedNote}`;
    } else {
      throw new RangeError(`Subframe type ${t.toString(2).padStart(6, '0')} is reserved.`);
    }
    res.endBit = r.pos;
  } catch (e) {
    res.type = 'flac.subframe.error';
    res.error = e instanceof Error ? e.message : String(e);
    res.summary = 'Could not decode';
  }
  return res;
}

function decodeResidual(
  r: FastBits, fb: Uint8Array, blockSize: number, order: number,
  add: (name: string, value: string, bit: number, bits: number, note?: string, raw?: number) => void,
): { method: string; partitionOrder: number } {
  let p = r.pos;
  const m = r.read(2);
  if (m > 1) throw new RangeError(`Residual coding method ${m} is reserved.`);
  const method = m ? 'Rice2' : 'Rice';
  add('Residual coding', m ? 'Rice2 (5-bit parameters)' : 'Rice (4-bit parameters)', p, 2,
    'The residual is the error left after prediction. Rice codes store small numbers in few bits.', m);
  p = r.pos;
  const po = r.read(4);
  const parts = 1 << po;
  add('Partition order', `${po} → ${plural(parts, 'partition')}`, p, 4,
    'The residual splits into 2^order parts. Each part gets its own Rice parameter, to follow changes in loudness.', po);
  if (blockSize % parts !== 0 || (blockSize >> po) < order) throw new RangeError(`Partition order ${po} does not fit a block of ${blockSize} samples.`);
  const per = blockSize >> po;
  const pBits = m ? 5 : 4;
  const escape = m ? 31 : 15;
  let groupStart = -1;
  for (let i = 0; i < parts; i++) {
    const n = i === 0 ? per - order : per;
    const detail = i < MAX_PARTITION_FIELDS;
    if (!detail && groupStart < 0) groupStart = r.pos;
    p = r.pos;
    const k = r.read(pBits);
    if (k === escape) {
      if (detail) add(`Partition ${i + 1} parameter`, 'Escape', p, pBits, 'An escape code: this part stores its residuals as plain signed numbers, not Rice codes.', k);
      p = r.pos;
      const raw = r.read(5);
      if (detail) add(`Partition ${i + 1} raw size`, `${raw} bits per residual`, p, 5, raw === 0 ? 'Zero bits: every residual in this part is 0.' : 'Size of each plain residual.', raw);
      p = r.pos;
      r.skip(raw * n);
    } else {
      if (detail) add(`Partition ${i + 1} Rice parameter`, `k = ${k}`, p, pBits,
        i === 0 ? `Each residual stores its low ${k} bits as-is and the rest as a run of zeros ended by a 1.` : undefined, k);
      p = r.pos;
      r.pos = skipRice(fb, r.pos, n, k);
    }
    if (detail) add(`Partition ${i + 1} residuals`, `${plural(n, 'sample')} in ${plural(r.pos - p, 'bit')}`, p, r.pos - p,
      i === 0 && order > 0 ? `The first part holds ${order} fewer residuals, because the warm-up samples need none.` : undefined);
  }
  if (groupStart >= 0) add(`Partitions ${MAX_PARTITION_FIELDS + 1}–${parts}`, `${plural(parts - MAX_PARTITION_FIELDS, 'partition')} in ${plural(r.pos - groupStart, 'bit')}`, groupStart, r.pos - groupStart);
  return { method, partitionOrder: po };
}

// ---------------------------------------------------------------------------
// Lazy per-frame decode.

function frameHeaderFields(b: Uint8Array, h: FrameHeader, si: StreamInfo | null): Field[] {
  const bf = new BitFields(b, h.start);
  bf.read('Sync code', 14, () => '11111111111110 (binary)', 'Marks the start of a frame. A reader can find frames by this pattern alone.');
  bf.read('Reserved', 1, undefined, 'Always 0.');
  bf.read('Blocking strategy', 1, v => (v ? 'Variable block size' : 'Fixed block size'),
    'Fixed: frames count by frame number. Variable: each frame gives its first sample number.');
  bf.read('Block size', 4, v => (v === 6 ? `8 bits at end of header → ${h.blockSize}` : v === 7 ? `16 bits at end of header → ${h.blockSize}` : `${h.blockSize.toLocaleString('en-US')} samples`),
    'How many samples per channel this frame holds. Codes 6 and 7 put the exact size after the frame number.');
  bf.read('Sample rate', 4, v => (v === 0 ? `From STREAMINFO (${si ? fmtHz(si.sampleRate) : 'unknown'})`
    : v >= 12 ? `At end of header → ${fmtHz(h.sampleRate)}` : fmtHz(h.sampleRate)),
  'Code 0 means "same as STREAMINFO". Codes 12–14 put an uncommon rate after the frame number.');
  bf.read('Channel assignment', 4, v => ASSIGN_NAMES[v] ?? 'Reserved',
    'How the channels are coded. Side = left − right, which is small when both sides sound alike.');
  bf.read('Sample size', 3, v => (v === 0 ? `From STREAMINFO (${si?.bps ?? '?'} bits)` : `${BPS_TABLE[v]} bits`), 'Bits per sample. Code 0 means "same as STREAMINFO".');
  bf.read('Reserved', 1, undefined, 'Always 0.');
  const fields = bf.fields;
  let q = h.start + 4;
  fields.push(field(h.variable ? 'Sample number' : 'Frame number', h.number.toLocaleString('en-US'), q, h.numLen,
    `Coded like a UTF-8 character: ${plural(h.numLen, 'byte')} here. Small numbers take 1 byte; big ones take up to 7.`, h.number));
  q += h.numLen;
  if (h.bsCode === 6 || h.bsCode === 7) {
    const n = h.bsCode === 6 ? 1 : 2;
    fields.push(field('Block size − 1', `${h.blockSize - 1} → ${h.blockSize} samples`, q, n, 'The exact block size, for sizes not in the 4-bit table.', h.blockSize - 1));
    q += n;
  }
  if (h.srCode >= 12) {
    const n = h.srCode === 12 ? 1 : 2;
    const unit = h.srCode === 12 ? 'kHz' : h.srCode === 13 ? 'Hz' : 'tens of Hz';
    fields.push(field(`Sample rate (${unit})`, fmtHz(h.sampleRate), q, n, 'The exact sample rate, for rates not in the 4-bit table.', h.srCode === 12 ? b[q] : u16be(b, q)));
    q += n;
  }
  fields.push(field('CRC-8', hex(b[q]), q, 1, 'A checksum of the header bytes before it. A match shows that the header is not damaged.', b[q]));
  return fields;
}

function decodeFrame(ctx: Ctx, seg: Segment, h: FrameHeader): { fields: Field[]; children: Segment[] } {
  const hit = ctx.cache.get(seg);
  if (hit) return hit;
  const { b, si } = ctx;
  const { start, end } = seg;
  const hdrFields = frameHeaderFields(b, h, si);
  const children: Segment[] = [{
    kind: 'header', type: 'flac.frame.header', name: 'Frame header', start, end: start + h.headerLen,
    summary: `${plural(h.blockSize, 'sample')} · ${ASSIGN_SHORT[h.assignment]} · ${h.bps}-bit`, fields: hdrFields,
  }];
  const crcStart = end - 2;
  const fb = b.subarray(start, end);
  const base = start * 8;
  const side = sideIndex(h.assignment);

  // Decode each subframe in turn; each starts at the bit where the last one ended.
  const subs: SubframeResult[] = [];
  let pos = h.headerLen * 8;
  for (let ch = 0; ch < h.channels; ch++) {
    const s = decodeSubframe(fb, pos, h.blockSize, h.bps + (ch === side ? 1 : 0), base);
    subs.push(s);
    if (s.error) break;
    pos = s.endBit;
  }

  // Subframes share bytes at their edges. Segments may not overlap, so a shared
  // byte goes to the later subframe; a field that runs into it is cut at the
  // byte edge (with a note), or moved to the later subframe if it lies fully inside.
  let segStart = start + h.headerLen;
  let carry: Field[] = [];
  let lastEnd = segStart;
  let decodedEnd = -1; // absolute bit where the last subframe ended
  subs.forEach((s, i) => {
    const last = i === subs.length - 1;
    const absEnd = base + s.endBit;
    let segEnd = s.error ? Math.max(segStart, crcStart) : last ? Math.ceil(absEnd / 8) : Math.floor(absEnd / 8);
    segEnd = Math.max(segStart, Math.min(segEnd, end));
    const fields: Field[] = carry;
    carry = [];
    for (const f of s.fields) {
      if (f.bit + f.bits <= segEnd * 8) fields.push(f);
      else if (f.bit >= segEnd * 8) carry.push({ ...f, name: `${f.name} (end of subframe ${i + 1})` });
      else {
        const cut = f.bit + f.bits - segEnd * 8;
        fields.push({ ...f, bits: f.bits - cut, note: `${f.note ? f.note + ' ' : ''}Its last ${plural(cut, 'bit')} share a byte with the next subframe.` });
      }
    }
    if (last && !s.error) {
      decodedEnd = absEnd;
      const pad = (8 - (absEnd & 7)) & 7;
      if (pad && Math.ceil(absEnd / 8) <= end) {
        const v = new BitFields(b, 0, absEnd).peek(pad);
        fields.push({ name: 'Zero padding', value: `${pad} bits (${v.toString(2).padStart(pad, '0')})`, raw: v, bit: absEnd, bits: pad,
          note: v ? 'These bits should be 0. The frame may be damaged.' : 'Fills the last byte, so the CRC-16 footer starts on a byte boundary.' });
      }
    }
    const channel = channelLabel(h.assignment, i);
    const sb = h.bps + (i === side ? 1 : 0);
    children.push({
      kind: 'audio', type: s.type, name: `Subframe ${i + 1}: ${channel}`, start: segStart, end: segEnd,
      summary: s.error ? 'Could not decode' : `${s.summary}${i === side ? ` · ${sb} bits/sample` : ''}`, fields,
      warning: s.error ? `This subframe could not be decoded: ${s.error}` : undefined,
    });
    segStart = segEnd;
    lastEnd = segEnd;
  });

  let warning: string | undefined;
  const fields = hdrFields.filter(f => f.name !== 'Sync code' && f.name !== 'Reserved');
  const footer = (at: number) => {
    const stored = u16be(b, at), calc = crc16(b, start, at);
    const f = field('CRC-16', `${hex(stored, 4)} · ${stored === calc ? 'matches' : `does not match (computed ${hex(calc, 4)})`}`, at, 2,
      'A checksum of the whole frame. A decoder uses it to detect damaged audio.', stored);
    children.push({ kind: 'header', type: 'flac.frame.footer', name: 'Frame footer (CRC-16)', start: at, end: at + 2, fields: [f], summary: stored === calc ? 'OK' : 'Mismatch' });
    fields.push(f);
  };
  if (decodedEnd >= 0 && lastEnd < crcStart && crc16(b, start, lastEnd) === u16be(b, lastEnd)) {
    // The frame really ends early: a valid CRC-16 follows the subframes, and
    // the bytes after it belong to no frame.
    footer(lastEnd);
    children.push({ kind: 'unknown', type: 'flac.frame.extra', name: 'Unexpected bytes', start: lastEnd + 2, end,
      summary: fmtBytes(end - lastEnd - 2), warning: 'The frame ends before these bytes (its CRC-16 matches). They belong to no frame: they may be damage, or the rest of a frame whose header was lost.' });
  } else {
    if (decodedEnd >= 0 && lastEnd < crcStart) {
      children.push({ kind: 'unknown', type: 'flac.frame.extra', name: 'Unexpected bytes', start: lastEnd, end: crcStart,
        summary: fmtBytes(crcStart - lastEnd), warning: 'The subframes end before this point, so these bytes do not fit the frame. They may be damage.' });
      lastEnd = crcStart;
    } else if (decodedEnd >= 0 && lastEnd > crcStart) {
      warning = 'The subframes run into the CRC-16 bytes, so this frame is damaged or cut short.';
    }
    if (lastEnd <= crcStart && crcStart >= start + h.headerLen) footer(crcStart);
  }
  if (warning) children[children.length - 1].warning ??= warning;
  const out = { fields, children };
  ctx.cache.set(seg, out);
  return out;
}

// ---------------------------------------------------------------------------
// Metadata blocks.

interface BlockResult {
  segment: Segment;
  tags?: Tag[];
  pictures?: Picture[];
  vendor?: string;
  si?: StreamInfo;
}

function parseStreamInfo(b: Uint8Array, p: number): { fields: Field[]; si: StreamInfo } {
  const bf = new BitFields(b, p);
  const minBlock = bf.read('Min block size', 16, v => `${v.toLocaleString('en-US')} samples`, 'The fewest samples in any frame, not counting the last frame.');
  const maxBlock = bf.read('Max block size', 16, v => `${v.toLocaleString('en-US')} samples`, 'The most samples in any frame. Equal min and max mean a fixed block size.');
  const minFrame = bf.read('Min frame size', 24, v => (v ? fmtBytes(v) : 'Unknown (0)'), 'The smallest frame in bytes. A reader can skip this far to look for the next frame.');
  const maxFrame = bf.read('Max frame size', 24, v => (v ? fmtBytes(v) : 'Unknown (0)'), 'The largest frame in bytes. A player can size its buffer from it.');
  const sampleRate = bf.read('Sample rate', 20, v => `${v.toLocaleString('en-US')} Hz`, 'Samples per second in each channel. 20 bits allow rates up to about 1 MHz.');
  const channels = bf.read('Channels', 3, v => `${v + 1} (${channelName(v + 1)})`, 'Stored as channels − 1, so 3 bits give 1 to 8 channels.') + 1;
  const bps = bf.read('Bits per sample', 5, v => `${v + 1} bits`, 'Stored as bits − 1, so 5 bits give 1 to 32 bits.') + 1;
  const totalSamples = bf.read('Total samples', 36, v => (v ? `${v.toLocaleString('en-US')} (${fmtSec(v / (sampleRate || 1))})` : 'Unknown (0)'),
    'Samples per channel in the whole stream. Divide by the sample rate to get the duration.');
  let md5 = '';
  let md5Set = false;
  for (let i = 0; i < 16; i++) { md5 += b[p + 18 + i].toString(16).padStart(2, '0'); if (b[p + 18 + i]) md5Set = true; }
  const fields = bf.fields;
  fields.push(field('MD5 signature', md5Set ? md5 : 'Not set (all zeros)', p + 18, 16, 'A fingerprint of the decoded audio. A decoder can use it to prove the decode is perfect.'));
  return { fields, si: { minBlock, maxBlock, minFrame, maxFrame, sampleRate, channels, bps, totalSamples, md5Set } };
}

function parseSeekTable(b: Uint8Array, p: number, end: number, audioStart: () => number, sr: () => number): Segment[] {
  const out: Segment[] = [];
  for (let i = 0; p + 18 <= end; i++, p += 18) {
    const placeholder = u32be(b, p) === 0xffffffff && u32be(b, p + 4) === 0xffffffff;
    const sample = u32be(b, p) * 4294967296 + u32be(b, p + 4);
    const off = u32be(b, p + 8) * 4294967296 + u32be(b, p + 12);
    const n = u16be(b, p + 16);
    const abs = audioStart() + off;
    out.push({
      kind: 'index', type: 'flac.seekpoint', name: `Seek point ${i + 1}`, start: p, end: p + 18,
      summary: placeholder ? 'Placeholder (unused)' : `${fmtSec(sample / (sr() || 1))} → byte ${abs.toLocaleString('en-US')}`,
      fields: placeholder
        ? [field('Sample number', 'Placeholder (0xFFFFFFFFFFFFFFFF)', p, 8, 'An empty slot. A tool can fill it later without rewriting the file.'),
          field('Byte offset', off, p + 8, 8), field('Frame samples', n, p + 16, 2)]
        : [field('Sample number', sample, p, 8, 'The first sample of the target frame.'),
          field('Byte offset', `${off.toLocaleString('en-US')} (file byte ${abs.toLocaleString('en-US')})`, p + 8, 8, 'Distance from the first audio frame to the target frame.', off),
          field('Frame samples', n, p + 16, 2, 'How many samples the target frame holds.')],
    });
  }
  return out;
}

function parseCueSheet(b: Uint8Array, p: number, end: number): { fields: Field[]; children: Segment[]; summary: string } {
  const fields: Field[] = [];
  const children: Segment[] = [];
  if (p + 396 > end) return { fields, children, summary: 'Too short' };
  const catalog = ascii(b, p, 128).replace(/\0+$/, '');
  fields.push(field('Media catalog number', catalog || '(none)', p, 128, 'The disc barcode (UPC/EAN), for CDs.'));
  const leadIn = u32be(b, p + 128) * 4294967296 + u32be(b, p + 132);
  fields.push(field('Lead-in samples', leadIn, p + 128, 8, 'Samples of silence before track 1 on a CD.'));
  const isCd = b[p + 136] >> 7;
  fields.push({ name: 'Is a CD', value: isCd ? 'Yes' : 'No', raw: isCd, bit: (p + 136) * 8, bits: 1, note: 'Set when this cue sheet comes from a CD.' });
  const nTracks = b[p + 395];
  fields.push(field('Track count', nTracks, p + 395, 1, 'Number of tracks, including the lead-out track.'));
  let q = p + 396;
  for (let t = 0; t < nTracks && q + 36 <= end; t++) {
    const tStart = q;
    const off = u32be(b, q) * 4294967296 + u32be(b, q + 4);
    const num = b[q + 8];
    const isrc = ascii(b, q + 9, 12).replace(/\0+$/, '');
    const flags = b[q + 21];
    const nIdx = b[q + 35];
    const tf: Field[] = [
      field('Track offset', `${off.toLocaleString('en-US')} samples`, q, 8, 'Where the track starts, in samples from the start of the audio.', off),
      field('Track number', num === 170 || num === 255 ? `${num} (lead-out)` : num, q + 8, 1, 'Track 170 (CD) or 255 marks the lead-out: the end of the disc.', num),
      field('ISRC', isrc || '(none)', q + 9, 12, 'International Standard Recording Code for this track.'),
      { name: 'Track type', value: flags & 0x80 ? 'Non-audio' : 'Audio', raw: flags >> 7, bit: (q + 21) * 8, bits: 1 },
      { name: 'Pre-emphasis', value: flags & 0x40 ? 'Yes' : 'No', raw: (flags >> 6) & 1, bit: (q + 21) * 8 + 1, bits: 1, note: 'An old CD treble boost that the player must undo.' },
      field('Index point count', nIdx, q + 35, 1),
    ];
    q += 36;
    const idx: Segment[] = [];
    for (let i = 0; i < nIdx && q + 12 <= end; i++, q += 12) {
      const io = u32be(b, q) * 4294967296 + u32be(b, q + 4);
      idx.push({
        kind: 'index', type: 'flac.cuesheet.index', name: `Index ${b[q + 8]}`, start: q, end: q + 12, summary: `+${io.toLocaleString('en-US')} samples`,
        fields: [field('Offset', `${io.toLocaleString('en-US')} samples`, q, 8, 'Offset from the track start.', io),
          field('Index number', b[q + 8], q + 8, 1, 'Index 0 is the pre-gap; index 1 is where the track really starts.')],
      });
    }
    children.push({ kind: 'index', type: 'flac.cuesheet.track', name: `Track ${num}`, start: tStart, end: q, summary: `starts at sample ${off.toLocaleString('en-US')}`, fields: tf, children: idx });
  }
  return { fields, children, summary: `${plural(nTracks, 'track')}${catalog ? ` · catalog ${catalog}` : ''}` };
}

// ---------------------------------------------------------------------------
// Trailing APEv2 tag (sometimes added by taggers; not part of FLAC).

function parseApeTag(b: Uint8Array, footer: number, minStart: number): { segment: Segment; tags: Tag[] } | null {
  if (!matches(b, footer, 'APETAGEX')) return null;
  const size = u32le(b, footer + 12), count = u32le(b, footer + 16), flags = u32le(b, footer + 20);
  const hasHeader = (flags & 0x80000000) !== 0;
  const itemsStart = footer + 32 - size;
  const start = itemsStart - (hasHeader ? 32 : 0);
  if (size < 32 || start < minStart) return null;
  const tags: Tag[] = [];
  const children: Segment[] = [];
  const hdr = (at: number, name: string): Segment => ({
    kind: 'metadata', type: 'flac.apetag.header', name, start: at, end: at + 32, fields: [
      field('Preamble', 'APETAGEX', at, 8), field('Version', u32le(b, at + 8), at + 8, 4, '2000 means APEv2.'),
      field('Tag size', u32le(b, at + 12), at + 12, 4, 'Size of the items plus the footer, in bytes.'),
      field('Item count', u32le(b, at + 16), at + 16, 4), field('Flags', hex(u32le(b, at + 20), 8), at + 20, 4)],
  });
  if (hasHeader) children.push(hdr(start, 'APE tag header'));
  let p = itemsStart;
  for (let i = 0; i < count && p + 9 <= footer; i++) {
    const len = u32le(b, p), iflags = u32le(b, p + 4);
    let k = p + 8;
    while (k < footer && b[k] !== 0) k++;
    const key = ascii(b, p + 8, k - p - 8);
    const vEnd = k + 1 + len;
    if (vEnd > footer) break;
    const text = ((iflags >> 1) & 3) === 0;
    const value = text ? utf8Str(b, k + 1, Math.min(len, 4096)) : `${fmtBytes(len)} of binary data`;
    if (text) tags.push({ key, value, rawKey: key });
    children.push({ kind: 'metadata', type: 'flac.apetag.item', name: key, start: p, end: vEnd, summary: value.slice(0, 80),
      fields: [field('Value size', len, p, 4), field('Item flags', hex(iflags, 8), p + 4, 4), field('Key', key, p + 8, k - p - 7), field('Value', value, k + 1, len)] });
    p = vEnd;
  }
  children.push(hdr(footer, 'APE tag footer'));
  return { segment: { kind: 'metadata', type: 'flac.apetag', name: 'APEv2 tag', start, end: footer + 32, summary: plural(count, 'item'), children }, tags };
}

// ---------------------------------------------------------------------------
// Main parse.

/** Offset of "fLaC" after any ID3v2 tags (and zero padding after them), or -1. */
function findMarker(b: Uint8Array): number {
  let p = 0;
  for (let k = 0; k < 8 && matches(b, p, 'ID3') && p + 10 <= b.length; k++) {
    p += 10 + synchsafe32(b, p + 6) + (b[p + 3] === 4 && b[p + 5] & 0x10 ? 10 : 0);
  }
  if (matches(b, p, 'fLaC')) return p;
  if (p === 0) return -1;
  for (let i = p, lim = Math.min(b.length - 4, p + 65536); i <= lim; i++) if (matches(b, i, 'fLaC')) return i;
  return -1;
}

function parse(b: Uint8Array): ParsedFile {
  const size = b.length;
  const segments: Segment[] = [];
  const tags: Tag[] = [];
  const pictures: Picture[] = [];
  const notes: Note[] = [];
  const warns: Note[] = [];
  const warn = (text: string) => warns.push({ level: 'warn', text });

  // Leading ID3v2 tag(s). FLAC does not define them, but some tools write them.
  let p = 0;
  for (let k = 0; k < 8; k++) {
    const r = parseId3v2(b, p);
    if (!r || r.end <= p) break;
    segments.push(r.segment);
    tags.push(...r.tags);
    pictures.push(...r.pictures);
    notes.push(...r.notes);
    p = r.end;
  }
  if (p > 0) warn('This file starts with an ID3v2 tag. FLAC does not define ID3 tags, so some players may not read it (or may fail on it). FLAC uses Vorbis comments instead.');
  const marker = matches(b, p, 'fLaC') ? p : findMarker(b);
  if (marker < 0) throw new ParseError('No "fLaC" marker found.');
  if (marker > p) {
    segments.push({ kind: 'unknown', type: 'flac.junk', name: 'Unknown bytes', start: p, end: marker, summary: fmtBytes(marker - p) });
  }
  segments.push({
    kind: 'header', type: 'flac.marker', name: 'fLaC marker', start: marker, end: marker + 4, summary: '"fLaC"',
    fields: [field('Signature', 'fLaC', marker, 4, 'These 4 bytes identify a FLAC stream.')],
  });

  // Metadata blocks.
  let si: StreamInfo | null = null;
  let vendor: string | undefined;
  let audioStart = marker + 4;
  let seekPoints = 0, placeholders = 0, paddingBytes = 0, blockCount = 0;
  p = marker + 4;
  const getAudioStart = () => audioStart;
  const getRate = () => si?.sampleRate ?? 0;
  while (p + 4 <= size) {
    const hdr = b[p];
    const last = hdr >> 7, type = hdr & 0x7f;
    const len = u24be(b, p + 1);
    if (type === 127) { warn(`A metadata block header at byte ${p.toLocaleString('en-US')} has the invalid type 127. Parsing of metadata stops there.`); break; }
    const body = p + 4;
    let end = body + len;
    let warning: string | undefined;
    if (end > size) { end = size; warning = `The block says it is ${fmtBytes(len)} long, but the file ends first.`; }
    const info = BLOCK_TYPES[type];
    const bh = new BitFields(b, p);
    bh.read('Last block', 1, v => (v ? 'Yes: audio frames follow' : 'No: more blocks follow'), 'Set on the last metadata block.');
    bh.read('Block type', 7, v => `${BLOCK_TYPES[v]?.name ?? 'Reserved'} (${v})`, 'What this block holds. Types 7–126 are reserved for the future.');
    bh.read('Length', 24, v => `${v.toLocaleString('en-US')} bytes`, 'Size of the block body after this 4-byte header.');
    const header: Segment = { kind: 'header', type: 'flac.block.header', name: 'Block header', start: p, end: body, fields: bh.fields,
      summary: `${info?.name ?? `type ${type}`} · ${fmtBytes(len)}${last ? ' · last' : ''}` };
    const seg: Segment = {
      kind: info?.kind ?? 'unknown', type: info?.type ?? 'flac.block.unknown', name: info?.label ?? `Reserved block (type ${type})`,
      start: p, end, children: [header], warning,
    };
    try {
      const r = parseBlockBody(b, type, body, end, len, seg, getAudioStart, getRate);
      if (r.si) {
        if (si) warn('The file has more than one STREAMINFO block. Only the first one counts.');
        else si = r.si;
      }
      if (type === 0 && blockCount > 0) warn('STREAMINFO is not the first metadata block, which the format requires.');
      if (r.tags) tags.push(...r.tags);
      if (r.pictures) pictures.push(...r.pictures);
      if (r.vendor !== undefined && vendor === undefined) vendor = r.vendor;
      if (type === 1) paddingBytes += len;
      if (type === 3) { seekPoints += Math.floor(len / 18); placeholders += r.placeholders ?? 0; }
    } catch {
      seg.warning = 'This block could not be read. It may be damaged.';
    }
    segments.push(seg);
    blockCount++;
    p = end;
    audioStart = end;
    if (last) break;
  }
  if (!si) warn('The file has no valid STREAMINFO block, so some stream facts come from the frame headers.');

  // Trailing tags: ID3v1 ("TAG", last 128 bytes) and APEv2 (footer "APETAGEX").
  let audioEnd = size;
  const trailing: Segment[] = [];
  if (size - 128 >= audioStart) {
    const v1 = parseId3v1(b, size - 128);
    if (v1) { trailing.unshift(v1.segment); tags.push(...v1.tags); audioEnd = size - 128; notes.push({ level: 'info', text: 'An ID3v1 tag sits at the end of the file. FLAC does not define it, but it does no harm.' }); }
  }
  if (audioEnd - 32 >= audioStart) {
    const ape = parseApeTag(b, audioEnd - 32, audioStart);
    if (ape) { trailing.unshift(ape.segment); tags.push(...ape.tags); audioEnd = ape.segment.start; notes.push({ level: 'info', text: 'An APEv2 tag sits at the end of the file. It is not part of FLAC, and many players ignore it.' }); }
  }

  // ---- Frame scan ----
  const hs = scanFrames(b, audioStart, audioEnd, si);

  const ctx: Ctx = { b, si, cache: new WeakMap() };
  const frames: Segment[] = [];
  const first = hs[0];
  const sr = si?.sampleRate || hs.find(h => h.sampleRate > 0)?.sampleRate || 0;
  if (!sr && hs.length) warn('No sample rate is given, so frame times assume 44,100 Hz.');
  const rate = sr || 44100;
  const variable = first?.variable ?? false;
  const fixedBS = si && si.minBlock === si.maxBlock && si.maxBlock >= 16 ? si.maxBlock : first?.blockSize ?? 4096;
  let prevEndSample = 0;
  const gaps: string[] = [];
  let crcFails = 0, firstCrcFail = '', backwards = 0;
  for (let i = 0; i < hs.length; i++) {
    const h = hs[i];
    const start = h.start, end = i + 1 < hs.length ? hs[i + 1].start : audioEnd;
    let s0 = variable ? h.number : h.number * fixedBS;
    if (i > 0) {
      const prev = hs[i - 1];
      const exp = expectedAfter(prev);
      if (h.number !== exp) {
        if (h.number > exp) {
          const missing = variable ? `${(h.number - exp).toLocaleString('en-US')} samples` : plural(h.number - exp, 'frame');
          gaps.push(`${variable ? `Sample number jumps from ${exp.toLocaleString('en-US')} to ${h.number.toLocaleString('en-US')}` : `Frame numbers jump from ${prev.number.toLocaleString('en-US')} to ${h.number.toLocaleString('en-US')}`} near byte ${start.toLocaleString('en-US')}: ${missing} missing or damaged.`);
        }
      }
    }
    if (s0 < prevEndSample) { backwards++; s0 = prevEndSample; }
    const s1 = s0 + h.blockSize;
    prevEndSample = s1;
    const seg: Segment = {
      kind: 'audio', type: 'flac.frame', name: `Frame ${variable ? i : h.number}`, start, end,
      time: [s0 / rate, s1 / rate],
      summary: `${plural(h.blockSize, 'sample')} · ${ASSIGN_SHORT[h.assignment]}`,
    };
    // Frame CRC-16 covers everything from the sync code to just before the CRC.
    if (end - start >= h.headerLen + 2) {
      const stored = u16be(b, end - 2), calc = crc16(b, start, end - 2);
      if (stored !== calc) {
        crcFails++;
        if (!firstCrcFail) firstCrcFail = seg.name;
        seg.warning = `The CRC-16 check fails (stored ${hex(stored, 4)}, computed ${hex(calc, 4)}). The frame is damaged, cut short, or has other bytes after it.`;
      }
    } else {
      crcFails++;
      seg.warning = 'This frame is too short to hold its CRC-16.';
    }
    seg.fields = () => decodeFrame(ctx, seg, h).fields;
    seg.children = () => decodeFrame(ctx, seg, h).children;
    frames.push(seg);
  }

  if (first && first.start > audioStart) {
    segments.push({ kind: 'unknown', type: 'flac.junk', name: 'Unknown bytes', start: audioStart, end: first.start, summary: fmtBytes(first.start - audioStart),
      warning: 'These bytes sit between the metadata and the first frame. They are not valid FLAC data.' });
    warn(`${fmtBytes(first.start - audioStart)} of unknown data sit between the metadata and the first audio frame.`);
  }
  const audioBytes = frames.length ? audioEnd - frames[0].start : 0;
  const lastEndSample = prevEndSample;
  if (frames.length) {
    segments.push({
      kind: 'audio', type: 'flac.frames', name: 'Audio frames', start: frames[0].start, end: audioEnd, children: frames,
      summary: `${plural(frames.length, 'frame')} · ${fmtBytes(audioBytes)}`,
    });
  } else if (audioEnd > audioStart) {
    segments.push({ kind: 'unknown', type: 'flac.junk', name: 'Unknown bytes', start: audioStart, end: audioEnd, summary: fmtBytes(audioEnd - audioStart) });
    warn('No valid audio frames were found.');
  }
  segments.push(...trailing);

  // ---- Audio info ----
  const channels = si?.channels ?? first?.channels;
  const bps = si?.bps ?? first?.bps;
  const totalSamples = si?.totalSamples || lastEndSample || undefined;
  const duration = totalSamples !== undefined && sr ? totalSamples / sr : undefined;
  const audio: AudioInfo = {
    codec: 'FLAC', codecLong: 'Free Lossless Audio Codec', lossless: true, bitrateMode: 'Lossless',
    sampleRate: sr || undefined, channels, bitsPerSample: bps, totalSamples, duration,
    channelMode: channels ? channelName(channels) : undefined,
    bitrate: duration ? Math.round((audioBytes * 8) / duration) : undefined,
    encoder: vendor,
  };

  // ---- Notes ----
  if (duration && channels && bps && totalSamples) {
    const raw = (totalSamples * channels * bps) / 8;
    const pct = Math.round((audioBytes / raw) * 100);
    notes.push({ level: 'info', text: `This FLAC is ${pct}% of the size of the same audio as raw PCM: ${fmtBytes(audioBytes)} of frames instead of ${fmtBytes(raw)} (${bps}-bit, ${channelName(channels).toLowerCase()}). The average bitrate is ${fmtKbps(audio.bitrate ?? 0)}.` });
  }
  if (si) {
    notes.push(si.md5Set
      ? { level: 'info', text: 'STREAMINFO holds an MD5 fingerprint of the audio. A decoder can use it to prove that it got back every sample exactly.' }
      : { level: 'info', text: 'The MD5 fingerprint in STREAMINFO is not set (all zeros), so a decoder cannot prove a perfect decode.' });
  }
  if (frames.length) {
    notes.push(variable
      ? { level: 'info', text: 'This stream uses a variable block size: the encoder picks a frame length to suit the sound. Each frame header gives its first sample number.' }
      : { level: 'info', text: `This stream uses a fixed block size of ${plural(fixedBS, 'sample')} per frame. Only the last frame can be shorter.` });
  }
  if (seekPoints) {
    notes.push({ level: 'info', text: `A seek table lists ${plural(seekPoints, 'point')}, so a player can jump close to any time without a search.${placeholders ? ` ${plural(placeholders, 'point')} ${placeholders === 1 ? 'is a placeholder' : 'are placeholders'}.` : ''}` });
  } else if (frames.length > 1) {
    notes.push({ level: 'info', text: 'There is no seek table. To seek, a player must search for frames by their sync code.' });
  }
  if (paddingBytes) notes.push({ level: 'info', text: `${fmtBytes(paddingBytes)} of padding is reserved, so tags can grow without rewriting the whole file.` });
  for (const g of gaps.slice(0, 5)) warn(g);
  if (gaps.length > 5) warn(`There are ${gaps.length - 5} more gaps in the frame numbers.`);
  if (backwards) warn(`${plural(backwards, 'frame')} ${backwards === 1 ? 'has' : 'have'} a number lower than the frame before, so ${backwards === 1 ? 'its' : 'their'} time is counted from the previous frame.`);
  if (crcFails) warn(`${plural(crcFails, 'frame')} ${crcFails === 1 ? 'fails its' : 'fail their'} CRC-16 check (first: ${firstCrcFail}). ${crcFails === 1 ? 'That frame is' : 'Those frames are'} damaged, or other bytes are mixed in.`);
  if (si?.totalSamples && frames.length && lastEndSample !== si.totalSamples) {
    warn(`STREAMINFO says ${si.totalSamples.toLocaleString('en-US')} samples, but the frames end at sample ${lastEndSample.toLocaleString('en-US')}. The file may be cut short or damaged.`);
  }

  return {
    format: 'flac', container: 'FLAC', size, segments, frames, audio, tags, pictures, notes: [...warns, ...notes],
  };
}

function expectedAfter(h: FrameHeader): number {
  return h.variable ? h.number + h.blockSize : h.number + 1;
}

/**
 * Finds the frames. A candidate must have the sync code, a valid CRC-8, and
 * values that agree with STREAMINFO. After a frame we skip ahead by the minimum
 * frame size. If the next frame does not have the expected number, we check
 * more carefully, because a false sync inside audio data is possible.
 */
function scanFrames(b: Uint8Array, audioStart: number, audioEnd: number, si: StreamInfo | null): FrameHeader[] {
  const hs: FrameHeader[] = [];
  let varBit = -1; // blocking strategy bit, fixed once the first frame is found
  const skip = si && si.minFrame >= 10 ? si.minFrame : 0;

  const findNext = (from: number, before: number): FrameHeader | null => {
    let i = from;
    while (i < before) {
      i = b.indexOf(0xff, i);
      if (i < 0 || i >= before) return null;
      const n = b[i + 1];
      if ((n & 0xfe) === 0xf8 && (varBit < 0 || (n & 1) === varBit)) {
        const h = parseFrameHeader(b, i, audioEnd, si);
        if (h && consistent(h, si)) return h;
      }
      i++;
    }
    return null;
  };
  const after = (h: FrameHeader) => h.start + Math.max(skip, h.headerLen + 2);
  const crcOk = (start: number, end: number) => end - start >= 8 && crc16(b, start, end - 2) === u16be(b, end - 2);
  /** True when `h` is a real frame even though its number is unexpected. */
  const confirm = (prev: FrameHeader | null, h: FrameHeader): boolean => {
    if (prev && crcOk(prev.start, h.start)) return true;
    const nx = findNext(after(h), audioEnd);
    if (nx) return nx.number === expectedAfter(h) || crcOk(h.start, nx.start);
    return crcOk(h.start, audioEnd);
  };

  let h = findNext(audioStart, audioEnd);
  while (h) {
    const prev = hs.length ? hs[hs.length - 1] : null;
    if (!prev) {
      if (h.start !== audioStart && !confirm(null, h)) { h = findNext(h.start + 1, audioEnd); continue; }
      varBit = h.variable ? 1 : 0;
    } else if (h.number !== expectedAfter(prev)) {
      // Maybe we skipped a real frame (a wrong min frame size), or a frame is lost.
      let close = findNext(prev.start + prev.headerLen + 2, h.start);
      while (close && close.number !== expectedAfter(prev)) close = findNext(close.start + 1, h.start);
      if (close) h = close;
      else if (!confirm(prev, h)) { h = findNext(h.start + 1, audioEnd); continue; }
    }
    hs.push(h);
    h = findNext(after(h), audioEnd);
  }
  return hs;
}

function parseBlockBody(
  b: Uint8Array, type: number, body: number, end: number, len: number, seg: Segment,
  audioStart: () => number, rate: () => number,
): BlockResult & { placeholders?: number } {
  const header = (seg.children as Segment[])[0];
  switch (type) {
    case 0: {
      if (len < 34 || end - body < 34) { seg.warning ??= 'STREAMINFO must be 34 bytes long.'; return { segment: seg }; }
      const { fields, si } = parseStreamInfo(b, body);
      seg.fields = fields;
      seg.summary = `${fmtHz(si.sampleRate)} · ${si.bps}-bit · ${channelName(si.channels)}${si.sampleRate ? ` · ${fmtSec(si.totalSamples / si.sampleRate)}` : ''}`;
      return { segment: seg, si };
    }
    case 1: {
      seg.summary = fmtBytes(len);
      seg.fields = len ? [field('Padding', `${len.toLocaleString('en-US')} bytes`, body, end - body, 'Empty space, all zeros. Tag editors can use it without rewriting the audio.')] : [];
      for (let i = body; i < end; i++) if (b[i]) { seg.warning ??= 'This padding holds non-zero bytes.'; break; }
      return { segment: seg };
    }
    case 2: {
      const id = ascii(b, body, 4);
      const known = APP_IDS[id];
      seg.summary = `"${id}"${known ? ` · ${known}` : ''}`;
      seg.fields = [field('Application ID', `"${id}"${known ? ` (${known})` : ' (not registered)'}`, body, 4, 'A registered 4-byte code that names the program that owns this data.')];
      if (end > body + 4) {
        seg.fields.push(field('Data', fmtBytes(end - body - 4), body + 4, end - body - 4, 'Private data. Only the named program knows its meaning.'));
        (seg.children as Segment[]).push({ kind: 'metadata', type: 'flac.application.data', name: 'Application data', start: body + 4, end, summary: fmtBytes(end - body - 4) });
      }
      return { segment: seg };
    }
    case 3: {
      const n = Math.floor((end - body) / 18);
      let ph = 0;
      for (let i = 0, q = body; i < n; i++, q += 18) if (u32be(b, q) === 0xffffffff && u32be(b, q + 4) === 0xffffffff) ph++;
      seg.summary = `${plural(n, 'seek point')}${ph ? ` (${ph} placeholder${ph === 1 ? '' : 's'})` : ''}`;
      seg.fields = n ? [field('Seek points', n, body, n * 18, 'Each point is 18 bytes: a sample number, a byte offset and a frame length.')] : [];
      if ((end - body) % 18) seg.warning ??= 'The seek table length is not a multiple of 18 bytes.';
      // Seek points can number in the thousands, so build them only on demand.
      seg.children = () => [header, ...parseSeekTable(b, body, end, audioStart, rate)];
      return { segment: seg, placeholders: ph };
    }
    case 4: {
      const r = parseVorbisComment(b, body, end);
      const kids = r.segments.filter(s => s.end <= end);
      if (kids.length < r.segments.length) seg.warning ??= 'Part of the comment block runs past its end.';
      (seg.children as Segment[]).push(...kids);
      seg.fields = r.fields.filter(f => (f.bit + f.bits) / 8 <= end);
      seg.summary = `${plural(r.tags.length, 'tag')} · ${r.vendor}`;
      return { segment: seg, tags: r.tags, pictures: r.pictures, vendor: r.vendor };
    }
    case 5: {
      const r = parseCueSheet(b, body, end);
      seg.fields = r.fields;
      seg.summary = r.summary;
      (seg.children as Segment[]).push(...r.children);
      return { segment: seg };
    }
    case 6: {
      const r = parseFlacPicture(b, body);
      if (r.picture.end > end || r.children.some(c => c.end > end)) {
        seg.warning ??= 'The picture data runs past the end of its block.';
        return { segment: seg };
      }
      (seg.children as Segment[]).push(...r.children);
      seg.fields = r.fields;
      seg.image = { mime: r.picture.mime, start: r.picture.start, end: r.picture.end };
      seg.summary = `${r.picture.pictureType ?? 'Picture'} · ${r.picture.mime} · ${fmtBytes(r.picture.end - r.picture.start)}`;
      return { segment: seg, pictures: [r.picture] };
    }
    default:
      seg.summary = fmtBytes(len);
      if (end > body) seg.fields = [field('Body', fmtBytes(end - body), body, end - body, 'Data of a block type this parser does not know. A decoder skips it by its length.')];
      return { segment: seg };
  }
}

// ---------------------------------------------------------------------------

const flacExplanations: Record<string, Explanation> = {
  'flac.marker': {
    title: 'fLaC marker',
    body: 'Every FLAC stream starts with the 4 ASCII bytes `fLaC`. A program reads them to know that the file is FLAC.',
  },
  'flac.block.header': {
    title: 'Metadata block header',
    body: 'Four bytes before each metadata block. They give a **last block** flag, the block type, and the body length. A reader can skip any block it does not need by its length.',
  },
  'flac.streaminfo': {
    title: 'STREAMINFO',
    body: 'The one block that every FLAC file must have, and always the first. It gives the sample rate, channel count, bit depth, total samples, and an MD5 fingerprint of the audio. The fields are packed at odd bit sizes (20-bit rate, 36-bit sample count) to save space.',
  },
  'flac.padding': {
    title: 'Padding',
    body: 'Empty space, all zeros. It lets a tag editor make tags or cover art bigger in place. Without padding, a small tag edit would mean a rewrite of the whole file.',
  },
  'flac.application': {
    title: 'APPLICATION block',
    body: 'Private data for one program. A registered 4-byte ID names the program. For example, `riff` holds WAV chunks that `flac --keep-foreign-metadata` keeps for a perfect round trip. Other programs skip it.',
  },
  'flac.application.data': {
    title: 'Application data',
    body: 'The private bytes of an APPLICATION block. Only the program named by the ID knows what they mean.',
  },
  'flac.seektable': {
    title: 'Seek table',
    body: 'A list of seek points. Each point says "the frame with sample N starts X bytes into the audio". A player uses it to jump to a time quickly, without a search through the frames.',
  },
  'flac.seekpoint': {
    title: 'Seek point',
    body: 'One entry in the seek table: a sample number, the byte offset of the frame that holds it (counted from the first frame), and the frame length. A placeholder point (all 1 bits) is an empty slot for later.',
  },
  'flac.vorbis_comment': {
    title: 'Vorbis comment block',
    body: 'The tags of a FLAC file: title, artist, album and so on, as `NAME=value` text. FLAC borrowed this simple format from Ogg Vorbis. The lengths are little-endian, which is unusual in big-endian FLAC.',
  },
  'flac.cuesheet': {
    title: 'Cue sheet',
    body: 'The track layout of a CD, stored inside the FLAC file. It lets one FLAC file hold a whole album and still know where each track starts.',
  },
  'flac.cuesheet.track': {
    title: 'Cue sheet track',
    body: 'One track: its start in samples, its number, its ISRC code, and its index points. The last track (number 170 or 255) is the lead-out, which marks the end.',
  },
  'flac.cuesheet.index': {
    title: 'Track index point',
    body: 'A position inside a track. Index 1 is where the track starts. Index 0, if present, marks the pre-gap, the pause before the track.',
  },
  'flac.picture': {
    title: 'PICTURE block',
    body: 'Embedded art, such as a front cover. A small header describes the image, and the raw JPEG or PNG bytes follow. A file can hold many pictures, each with a role.',
  },
  'flac.block.unknown': {
    title: 'Reserved metadata block',
    body: 'A block type that this parser does not know. The format reserves these types for the future. A decoder skips such blocks by their length, so old players still work.',
  },
  'flac.frames': {
    title: 'Audio frames',
    body: 'The compressed audio. Each frame holds a short block of samples for all channels and can be decoded alone. There is no index of frames, so a reader finds each one by its sync code.',
  },
  'flac.frame': {
    title: 'FLAC frame',
    body: 'One block of audio, often 4,096 samples (about 0.1 s). It has a header, one **subframe** per channel, and a CRC-16. Each subframe predicts its samples from earlier ones and stores only the small errors. That is where the compression comes from.',
  },
  'flac.frame.header': {
    title: 'Frame header',
    body: 'Starts with the 14-bit sync code `11111111111110`. Then it gives the block size, sample rate, channel coding, and sample size, often as short codes that mean "same as STREAMINFO". The frame number uses a UTF-8-style code. A CRC-8 lets a reader reject false sync codes.',
  },
  'flac.subframe.constant': {
    title: 'CONSTANT subframe',
    body: 'All samples in this channel have the same value, so the encoder stores that value once. Digital silence costs only a few bytes this way.',
  },
  'flac.subframe.verbatim': {
    title: 'VERBATIM subframe',
    body: 'The samples are stored raw, with no compression. The encoder uses this when prediction does not help, for example with white noise. It keeps FLAC files from growing larger than raw audio.',
  },
  'flac.subframe.fixed': {
    title: 'FIXED subframe',
    body: 'Predicts each sample from the last 0–4 samples with a fixed formula. For example, order 2 assumes the wave goes on in a straight line. Only the error (the **residual**) is stored, in Rice codes. It is simple and fast to decode.',
  },
  'flac.subframe.lpc': {
    title: 'LPC subframe',
    body: 'Linear prediction: the encoder finds weights (coefficients) that best predict each sample from up to 32 earlier ones. Music is smooth, so the predictions are close, and the errors are small numbers. **Rice codes** store small numbers in few bits, so the channel shrinks a lot.',
  },
  'flac.subframe.error': {
    title: 'Undecodable subframe',
    body: 'The parser could not follow the bits of this subframe. The frame is probably damaged, or the header values are wrong.',
  },
  'flac.frame.extra': {
    title: 'Unexpected bytes',
    body: 'The subframes end before the frame does. These bytes do not belong to this frame. They can be damage, or the remains of a frame whose header was lost.',
  },
  'flac.frame.footer': {
    title: 'Frame footer (CRC-16)',
    body: 'A 16-bit checksum of the whole frame. A decoder compares it to its own calculation. A mismatch means that the frame is damaged, and a player can then mute that part.',
  },
  'flac.junk': {
    title: 'Unknown bytes',
    body: 'Bytes that are not part of any FLAC structure. They may be damage or data added by another program.',
  },
  'flac.apetag': {
    title: 'APEv2 tag',
    body: 'A tag format from Monkey\'s Audio, sometimes added to the end of other files. FLAC does not define it, so many players ignore it. Vorbis comments are the normal FLAC tags.',
  },
  'flac.apetag.header': {
    title: 'APE tag header / footer',
    body: 'A 32-byte block that starts with `APETAGEX`. It gives the tag version, size, and item count. The footer lets a reader find the tag from the end of the file.',
  },
  'flac.apetag.item': {
    title: 'APE tag item',
    body: 'One tag: a value size, flags, a text key, and the value.',
  },
};

export const flacParser: FormatParser = {
  id: 'flac',
  detect: (b: Uint8Array) => findMarker(b) >= 0,
  parse,
  explanations: {
    // Fallbacks for ID3 tags; the ID3 module's own text wins when it exists.
    id3v2: { title: 'ID3v2 tag', body: 'A tag format made for MP3. Some programs add it to the start of FLAC files too, but FLAC does not define it. Some FLAC players skip it, and some fail on it.' },
    id3v1: { title: 'ID3v1 tag', body: 'An old 128-byte tag at the very end of a file, made for MP3. It holds short title, artist and album text.' },
    ...id3Explanations,
    ...vorbisCommentExplanations,
    ...flacExplanations,
  },
};
